import React from 'react'
import * as PhosphorIcons from '@phosphor-icons/react'

const TaxCodeIdentifierViewResult = ({ result }) => {
  const formatDate = (isoDate) => {
    try {
      return new Date(isoDate).toLocaleString();
    } catch (e) {
      return isoDate;
    }
  };

  const metadata = result?.metadata || {};
  const taxCodeAnalysis = result?.tax_code_analysis || {};
  const codesFound = Array.isArray(taxCodeAnalysis?.codes_found) ? taxCodeAnalysis.codes_found : [];
  const complianceFactors = taxCodeAnalysis?.compliance_factors || {};

  return (
    <div className="mt-4 overflow-x-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Left Column */}
        <div className="space-y-4">
          {/* Tax Codes Found Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-primary/10">
                  <PhosphorIcons.FileText size={20} weight="bold" className="text-primary"/>
                </div>
                <h3 className="card-title text-lg">Tax Codes Found</h3>
              </div>

              <div className="space-y-3">
                {codesFound.map((code, idx) => (
                  <div key={idx} className="p-3 bg-base-200 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium">{code.code}</span>
                      <span className="px-2 py-0.5 rounded-full text-xs bg-primary/10">
                        {Math.round((code.confidence || 0) * 100)}%
                      </span>
                    </div>
                    
                    <div className="text-sm space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-base-content/70">Category:</span>
                        <span className="capitalize">{code.category?.replace(/_/g, ' ')}</span>
                      </div>
                      
                      {code.details && (
                        <>
                          {code.details.jurisdiction && (
                            <div className="flex items-center gap-2">
                              <span className="text-base-content/70">Jurisdiction:</span>
                              <span>{code.details.jurisdiction}</span>
                            </div>
                          )}
                          
                          {code.details.description && (
                            <div className="text-base-content/70">
                              <span className="block mb-1">Description:</span>
                              <p className="text-base-content">{code.details.description}</p>
                            </div>
                          )}

                          {code.details.effective_date && (
                            <div className="flex items-center gap-2">
                              <span className="text-base-content/70">Effective Date:</span>
                              <span>{formatDate(code.details.effective_date)}</span>
                            </div>
                          )}

                          {code.details.expiration_date && (
                            <div className="flex items-center gap-2">
                              <span className="text-base-content/70">Expiration Date:</span>
                              <span>{formatDate(code.details.expiration_date)}</span>
                            </div>
                          )}

                          {Array.isArray(code.details.related_codes) && code.details.related_codes.length > 0 && (
                            <div>
                              <span className="text-base-content/70 block mb-1">Related Codes:</span>
                              <div className="flex flex-wrap gap-2">
                                {code.details.related_codes.map((relatedCode, idx) => (
                                  <span key={idx} className="px-2 py-0.5 bg-base-300 rounded-full text-xs">
                                    {relatedCode}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          {/* Compliance Factors Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-secondary/10">
                  <PhosphorIcons.CheckSquare size={20} weight="bold" className="text-secondary"/>
                </div>
                <h3 className="card-title text-lg">Compliance Factors</h3>
              </div>

              <div className="space-y-3">
                {/* Eligibility Status */}
                {complianceFactors?.eligibility_criteria && (
                  <div className="p-3 bg-base-200 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium">Eligibility Status</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        complianceFactors.eligibility_criteria.requirements_met
                          ? 'bg-success/20 text-success'
                          : 'bg-error/20 text-error'
                      }`}>
                        {complianceFactors.eligibility_criteria.requirements_met ? 'Requirements Met' : 'Requirements Not Met'}
                      </span>
                    </div>

                    {Array.isArray(complianceFactors.eligibility_criteria.conditional_requirements) && 
                     complianceFactors.eligibility_criteria.conditional_requirements.length > 0 && (
                      <div className="mt-2">
                        <span className="text-sm text-base-content/70 block mb-1">Conditional Requirements:</span>
                        <div className="flex flex-wrap gap-2">
                          {complianceFactors.eligibility_criteria.conditional_requirements.map((req, idx) => (
                            <span key={idx} className="px-2 py-0.5 bg-base-300 rounded-full text-xs">
                              {req}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Documentation Requirements */}
                {complianceFactors?.documentation_requirements && (
                  <div className="p-3 bg-base-200 rounded-lg">
                    <span className="font-medium block mb-2">Documentation Requirements</span>
                    <div className="space-y-2 text-sm">
                      {Array.isArray(complianceFactors.documentation_requirements.required_forms) && (
                        <div>
                          <span className="text-base-content/70 block mb-1">Required Forms:</span>
                          <div className="flex flex-wrap gap-2">
                            {complianceFactors.documentation_requirements.required_forms.map((form, idx) => (
                              <span key={idx} className="px-2 py-0.5 bg-primary/10 rounded-full text-xs">
                                {form}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {complianceFactors.documentation_requirements.retention_period && (
                        <div className="flex items-center gap-2">
                          <span className="text-base-content/70">Retention Period:</span>
                          <span>{complianceFactors.documentation_requirements.retention_period}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Analysis Metrics */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <span className="font-medium block mb-2">Analysis Metrics</span>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-base-content/70">Confidence Score</span>
                      <span className="px-2 py-0.5 rounded-full text-xs bg-success/20 text-success">
                        {Math.round((metadata?.confidence_score || 0) * 100)}%
                      </span>
                    </div>
                    
                    {metadata?.context_clarity && (
                      <div className="flex justify-between items-center">
                        <span className="text-base-content/70">Context Clarity</span>
                        <span className="capitalize">{metadata.context_clarity}</span>
                      </div>
                    )}
                    
                    {metadata?.analysis_timestamp && (
                      <div className="flex justify-between items-center">
                        <span className="text-base-content/70">Last Updated</span>
                        <span>{formatDate(metadata.analysis_timestamp)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxCodeIdentifierViewResult
